import axios from 'axios';
import { clearMerchantScopedState } from '@/util/ClientState';

class AuthService {
  // Step 1 (merchant). For a user with 2FA on, the backend sends an SMS code and returns
  // { result: "OTP_REQUIRED", phoneHint } instead of a token — call verifyLogin() next. phoneHint is a
  // PARTIAL reveal from the server (+47 •••• 1541): enough to tell which phone was texted, not enough
  // to dial. The full number is never sent, because this step is reachable with a stolen password.
  login(user) {
    return axios
      .post('/api/public/backoffice_login', {
        username: user.username.trim().toLowerCase(),
        password: user.password,
        // Travels WITH the credentials: backoffice_login verifies it before it reads a username, so the
        // check cannot be walked past by calling the endpoint directly.
        recaptchaToken: user.recaptchaToken
      })
      .then(response => {
        const data = response.data;
        if(data.result == "SUCCEEDED") {
          let u = JSON.parse(data.user);
          // Drop the previous merchant's cached state BEFORE adopting this session — a session
          // that expired or was lost never went through logout() at all.
          clearMerchantScopedState();
          localStorage.setItem('user', JSON.stringify(u));
          return { result: "SUCCEEDED", user: u };
        } else if(data.result == "OTP_REQUIRED") {
          return { result: "OTP_REQUIRED", phoneHint: data.phoneHint };
        }
        return { result: "FAILED", message: data.message };
      });
  }

  // Step 1: validate username/password. For SUPER_ADMIN with 2FA on, the backend sends an SMS code and
  // returns { result: "OTP_REQUIRED", phoneHint } instead of a token — call superAdminVerify() next.
  superAdminlogin(user) {
    return axios
        .post('/api/public/backoffice_login', {
          username: user.username,
          password: user.password,
          merchant: user.merchant,
          recaptchaToken: user.recaptchaToken
        })
        .then(response => {
          const data = response.data;
          if(data.result == "SUCCEEDED") {
            let u = JSON.parse(data.user);
            // Drop the previous merchant's cached state BEFORE adopting this session — a session
            // that expired or was lost never went through logout() at all.
            clearMerchantScopedState();
            localStorage.setItem('user', JSON.stringify(u));
            return { result: "SUCCEEDED", user: u };
          } else if(data.result == "OTP_REQUIRED") {
            return { result: "OTP_REQUIRED", phoneHint: data.phoneHint };
          }
          return { result: "FAILED", message: data.message };
        });
  }

  // Step 2 (shared by superadmin + merchant): verify the SMS code (the backend re-checks username/password)
  // and complete the login.
  verifyLogin(payload) {
    return axios
        .post('/api/public/backoffice_login_verify', {
          username: payload.username,
          password: payload.password,
          merchant: payload.merchant,
          code: payload.code
        })
        .then(response => {
          const data = response.data;
          if(data.result == "SUCCEEDED") {
            let u = JSON.parse(data.user);
            // Drop the previous merchant's cached state BEFORE adopting this session — a session
            // that expired or was lost never went through logout() at all.
            clearMerchantScopedState();
            localStorage.setItem('user', JSON.stringify(u));
            return { result: "SUCCEEDED", user: u };
          }
          return { result: "FAILED", message: data.message };
        });
  }

  logout() {
    // Best-effort server-side revocation of the session token, then clear local state.
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      if (user && user.authToken) {
        axios.post('/api/public/backoffice_logout', { token: user.authToken }).catch(() => {});
      }
    } catch (e) { /* ignore */ }
    // Not just `user`: every per-merchant cache goes, or the next merchant inherits it.
    clearMerchantScopedState();
  }

}

export default new AuthService();
